import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RiskBadge from '../../RiskBadge/RiskBadge';
import { Row, Col, Form, Table, Spinner } from 'react-bootstrap';
import CustomModal from '../../Shared/CustomModal/CustomModal';
import './PatientTable.css';
import { Images } from '../../../assets/assets';
import SvgIcon from '../../Shared/SvgIcon/SvgIcon';
import scanService from '../../../services/scanService';
import patientService from '../../../services/patientService';
import authService from '../../../services/authService';
import showToast from '../../../utils/toast';
import { FILE_UPLOAD, SUCCESS_MESSAGES, ERROR_MESSAGES } from '../../../constants';

export default function PatientTable({ rows = [] }) {
  const navigate = useNavigate();
  const [modalShow, setModalShow] = useState(false);
  const [scanData, setscanData] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFlairFile, setSelectedFlairFile] = useState(null);
  const [selectedT1File, setSelectedT1File] = useState(null);
  const [isDragOverFlair, setIsDragOverFlair] = useState(false);
  const [isDragOverT1, setIsDragOverT1] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '',
    age: '',
    sex: '',
    msSubType: '',
    edssScore: '',
  });
  const [patients, setPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [showCreatePatientForm, setShowCreatePatientForm] = useState(false);
  const [newPatientData, setNewPatientData] = useState({ name: '', email: '' });
  const [creatingPatient, setCreatingPatient] = useState(false);
  const [patientsWithScans, setPatientsWithScans] = useState([]);
  const [loadingScans, setLoadingScans] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [riskFilter, setRiskFilter] = useState('All Risk');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPatients, setTotalPatients] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [uploadedScanData, setUploadedScanData] = useState(null);
  const [recentScanImages, setRecentScanImages] = useState([]);
  const flairFileInputRef = useRef(null);
  const t1FileInputRef = useRef(null);
  const fetchTimeoutRef = useRef(null);
  const isFetchingRef = useRef(false);

  // Fetch patients with scan data on component mount
  useEffect(() => {
    fetchPatientsWithScans();
  }, []);

  // Fetch patients when modal opens
  useEffect(() => {
    if (modalShow) {
      fetchPatients('');
      setShowCreatePatientForm(false);
    }
  }, [modalShow]);

  // Refresh patient data when modal closes (in case a scan was uploaded)
  useEffect(() => {
    if (!modalShow && uploadedScanData) {
      // Modal was closed and we have uploaded scan data, refresh patient data
      fetchPatientsWithScans();
    }
  }, [modalShow, uploadedScanData]);

  // Fetch patients with their latest scan in a single optimized call
  const fetchPatientsWithScans = async (filters = {}) => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoadingScans(true);
      
      // Prepare query parameters for backend filtering
      const queryParams = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        risk: riskFilter !== 'All Risk' ? riskFilter : undefined,
        sortField: sortField,
        sortDirection: sortDirection
      };
      
      // Use patient service to get patients with latest scans
      const patientsResponse = await patientService.getPatientsWithLatestScan(queryParams);
      
      const patients = patientsResponse.data || [];
      
      // Update pagination info
      if (patientsResponse.total !== undefined) {
        setTotalPatients(patientsResponse.total);
        setTotalPages(patientsResponse.totalPages || Math.ceil(patientsResponse.total / itemsPerPage));
      }
      
      // Map patients to the format expected by the table
      const patientsWithScanData = patients.map((patient) => {
        const latestScan = patient.latestScan || null;
        const latestEdssScore = latestScan?.edssScore ?? null;
        let riskLevel = 'Low';
        if (latestEdssScore !== null && latestEdssScore !== undefined) {
          if (latestEdssScore >= 6) riskLevel = 'High';
          else if (latestEdssScore >= 3) riskLevel = 'Medium';
        }
        let status = 'Unknown';
        let statusClass = 'unknown';
        if (latestScan) {
          if (latestScan.status === 'completed') { status = 'Stable'; statusClass = 'stable'; }
          else if (latestScan.status === 'processing') { status = 'Processing'; statusClass = 'processing'; }
          else if (latestScan.status === 'failed') { status = 'Failed'; statusClass = 'failed'; }
        }
        const subtypeMap = { RRMS: 'Relapsing-Remitting MS', SPMS: 'Secondary-Progressive MS', PPMS: 'Primary-Progressive MS' };
        const msSubType = latestScan?.msSubType;
        const diagnosis = msSubType ? `${subtypeMap[msSubType] || msSubType}\n${msSubType}` : 'Unknown';
        
        return {
          id: patient.id || patient._id,
          patientId: patient.patientId,
          name: patient.name,
          fullName: patient.name,
          email: patient.email || 'N/A',
          meta: patient.email || 'N/A',
          age: latestScan?.age || 'N/A', // Age from latest scan
          sex: latestScan?.sex || 'N/A', // Sex from latest scan
          msSubType,
          diagnosis,
          edss: latestEdssScore !== null && latestEdssScore !== undefined ? String(latestEdssScore) : 'N/A',
          status,
          statusClass,
          risk: riskLevel,
          lastScan: latestScan?.timepoint || 'No scans',
          lastScanDate: latestScan?.createdAt ? new Date(latestScan.createdAt).toLocaleDateString('en-GB') : 'N/A',
          latestScanData: latestScan,
          totalScans: typeof patient.scanCount === 'number' ? patient.scanCount : 0
        };
      });
      
      setPatientsWithScans(patientsWithScanData);
    } catch (error) {
      console.error('Failed to fetch patients with scans:', error);
      showToast.error(error.message || 'Failed to load patient data');
    } finally {
      setLoadingScans(false);
      isFetchingRef.current = false;
    }
  };

  // Fetch patients from API (search functionality)
  const fetchPatients = async (searchTerm = '') => {
    try {
      setLoadingPatients(true);
      
      // Use patient service to search patients
      const response = await patientService.searchPatients(searchTerm);
      
      setPatients(response.data || []);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      showToast.error(error.message || 'Failed to load patients');
      setPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  // Create a new patient
  const handleCreatePatient = async () => {
    if (!newPatientData.name.trim()) {
      showToast.error('Patient name is required');
      return;
    }

    try {
      setCreatingPatient(true);
      const response = await patientService.createPatient({
        name: newPatientData.name.trim(),
        email: newPatientData.email.trim() || undefined,
      });

      showToast.success('Patient created successfully');
      
      // Add the new patient to the list
      const newPatient = response.data;
      setPatients(prev => [newPatient, ...prev]);
      
      // Select the newly created patient
      setFormData(prev => ({
        ...prev,
        patientId: newPatient.id,
      }));
      
      // Reset create form
      setNewPatientData({ name: '', email: '' });
      setShowCreatePatientForm(false);
    } catch (error) {
      console.error('Failed to create patient:', error);
      showToast.error(error.message || 'Failed to create patient');
    } finally {
      setCreatingPatient(false);
    }
  };

  // Fetch all patients when modal opens (no search needed)
  // Removed debounced search useEffect since we're using dropdown only

  function closeModal(){
    setModalShow(false);
    setscanData(false);
    setSelectedFlairFile(null);
    setSelectedT1File(null);
    setUploadProgress(0);
    setIsDragOverFlair(false);
    setIsDragOverT1(false);
    setFormData({ patientId: '', age: '', sex: '', msSubType: '', edssScore: '' });
    setShowCreatePatientForm(false);
    setNewPatientData({ name: '', email: '' });
    setUploadedScanData(null);
    setRecentScanImages([]);
  }

  // Fetch recent scan images for a patient (only current scan response)
  const fetchRecentScanImages = async (patientId) => {
    try {
      // Fetch the most recent scan for the patient
      const scansResponse = await scanService.getScansByPatient(patientId);
      if (scansResponse.data && scansResponse.data.length > 0) {
        // Get only the most recent scan
        const latestScan = scansResponse.data[0];
        try {
          const analysisResponse = await scanService.getAnalysisResults(latestScan._id);
          
          if (analysisResponse.data?.analysisResult?.consolidated_results?.[0]?.assetPaths) {
            const assetPaths = analysisResponse.data.analysisResult.consolidated_results[0].assetPaths;
            
            // Create array with images from the current scan only
            const currentScanImages = [
              {
                timepoint: latestScan.timepoint || 'T1',
                scanDate: latestScan.createdAt ? new Date(latestScan.createdAt).toLocaleDateString() : 'N/A',
                multiplanarImage: assetPaths.visualizationMultiplanarPng,
                axialImage: assetPaths.visualizationSliceAxialPng,
                coronalImage: assetPaths.visualizationSliceCoronalPng,
                sagittalImage: assetPaths.visualizationSliceSagittalPng,
                rotation3d: assetPaths.visualization3dRotationVideo
              }
            ];
            
            setRecentScanImages(currentScanImages);
          } else {
            setRecentScanImages([]);
          }
        } catch (error) {
          console.error(`Error fetching analysis for latest scan ${latestScan._id}:`, error);
          setRecentScanImages([]);
        }
      } else {
        setRecentScanImages([]);
      }
    } catch (error) {
      console.error('Error fetching current scan images:', error);
      setRecentScanImages([]);
    }
  };

  // Process selected file (used by both file input and drag & drop)
  const processFile = (file, fileType) => {
    if (!file) return;

    // Validate file size
    if (file.size > FILE_UPLOAD.MAX_SIZE) {
      showToast.error(ERROR_MESSAGES.FILE_TOO_LARGE);
      return;
    }

    // Validate file type
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const isValidExtension = FILE_UPLOAD.ALLOWED_EXTENSIONS.some(ext => 
      file.name.toLowerCase().endsWith(ext)
    );

    if (!isValidExtension && !FILE_UPLOAD.ALLOWED_TYPES.includes(file.type)) {
      showToast.error(ERROR_MESSAGES.INVALID_FILE);
      return;
    }

    if (fileType === 'flair') {
      setSelectedFlairFile(file);
    } else if (fileType === 't1') {
      setSelectedT1File(file);
    }
    showToast.info(`${fileType.toUpperCase()} file selected: ${file.name}`);
  };

  // Handle file selection from input
  const handleFileSelect = (e, fileType) => {
    const file = e.target.files[0];
    processFile(file, fileType);
  };

  // Handle drag and drop
  const handleDragOver = (e, fileType) => {
    e.preventDefault();
    if (fileType === 'flair') {
      setIsDragOverFlair(true);
    } else if (fileType === 't1') {
      setIsDragOverT1(true);
    }
  };

  const handleDragLeave = (e, fileType) => {
    e.preventDefault();
    if (fileType === 'flair') {
      setIsDragOverFlair(false);
    } else if (fileType === 't1') {
      setIsDragOverT1(false);
    }
  };

  const handleDrop = (e, fileType) => {
    e.preventDefault();
    if (fileType === 'flair') {
      setIsDragOverFlair(false);
    } else if (fileType === 't1') {
      setIsDragOverT1(false);
    }
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFile(files[0], fileType);
    }
  };

  // Handle click to open file dialog
  const handleUploadAreaClick = (fileType) => {
    if (fileType === 'flair' && flairFileInputRef.current && !uploading) {
      flairFileInputRef.current.click();
    } else if (fileType === 't1' && t1FileInputRef.current && !uploading) {
      t1FileInputRef.current.click();
    }
  };

  // Remove selected file
  const removeSelectedFile = (fileType) => {
    if (fileType === 'flair') {
      setSelectedFlairFile(null);
      if (flairFileInputRef.current) {
        flairFileInputRef.current.value = '';
      }
    } else if (fileType === 't1') {
      setSelectedT1File(null);
      if (t1FileInputRef.current) {
        t1FileInputRef.current.value = '';
      }
    }
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle scan upload
  const handleUploadScan = async () => {
    // Validation
    if (!formData.patientId) {
      showToast.error('Please select or create a patient');
      return;
    }

    if (!formData.age) {
      showToast.error('Please enter patient age');
      return;
    }

    if (!formData.sex) {
      showToast.error('Please select patient sex');
      return;
    }

    if (!formData.msSubType) {
      showToast.error('Please select MS sub type');
      return;
    }

    if (!formData.edssScore) {
      showToast.error('Please enter EDSS score');
      return;
    }

    if (!selectedFlairFile) {
      showToast.error('Please select a FLAIR file');
      return;
    }

    if (!selectedT1File) {
      showToast.error('Please select a T1 file');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      // Create FormData
      const uploadFormData = new FormData();
      uploadFormData.append('flairFile', selectedFlairFile);
      uploadFormData.append('t1File', selectedT1File);
      uploadFormData.append('patientId', formData.patientId);
      uploadFormData.append('age', formData.age);
      uploadFormData.append('sex', formData.sex);
      uploadFormData.append('msSubType', formData.msSubType);
      uploadFormData.append('edssScore', formData.edssScore);

      // Upload scan with progress tracking
      const response = await scanService.uploadScan(
        uploadFormData,
        (progress) => {
          setUploadProgress(progress);
        }
      );

      // Success
      showToast.success(response.message || SUCCESS_MESSAGES.UPLOAD_SUCCESS);
      
      // Store the uploaded scan data for display
      const selectedPatient = patients.find(p => p.id === formData.patientId);
      setUploadedScanData({
        patientId: selectedPatient?.patientId || 'PT0000',
        age: formData.age,
        sex: formData.sex,
        msSubType: formData.msSubType,
        edssScore: formData.edssScore,
        scanDate: new Date().toISOString().split('T')[0],
        patientName: selectedPatient?.name || 'Unknown Patient'
      });
      
      // Fetch recent scan images for the patient
      await fetchRecentScanImages(formData.patientId);
      
      // Refresh patient data immediately after successful upload
      await fetchPatientsWithScans();
      
      // Show scan data view
      setscanData(true);
      
      // Reset form
      setSelectedFlairFile(null);
      setSelectedT1File(null);
      setUploadProgress(0);
      if (flairFileInputRef.current) {
        flairFileInputRef.current.value = '';
      }
      if (t1FileInputRef.current) {
        t1FileInputRef.current.value = '';
      }

    } catch (error) {
      console.error('Upload error:', error);
      
      // Handle timeout errors specifically
      if (error.isTimeout) {
        showToast.error('Upload timed out after 30 minutes. The analysis is still processing on the server. Please check the status later.');
      } else {
        showToast.error(error.message || ERROR_MESSAGES.UPLOAD_FAILED);
      }
    } finally {
      setUploading(false);
    }
  };

  // Navigate to Analysis page with patient data
  const handleViewAnalysis = (patient) => {
    try {
      
      // Store patient data in sessionStorage for the Analysis page
      const patientDataToStore = {
        id: patient.id,
        patientId: patient.patientId,
        name: patient.name,
        fullName: patient.fullName,
        email: patient.email,
        age: patient.age,
        sex: patient.sex,
        msSubType: patient.msSubType,
        diagnosis: patient.diagnosis,
        latestScanData: patient.latestScanData,
        totalScans: patient.totalScans
      };
      
      sessionStorage.setItem('selectedPatient', JSON.stringify(patientDataToStore));
      
      // Dispatch custom event to notify AdminHeader
      window.dispatchEvent(new CustomEvent('patientSelected'));
      
      // Check authentication status and determine correct route
      const isAdminAuthenticated = authService.isAdminAuthenticated();
      const isUserAuthenticated = authService.isAuthenticated();
      
      // Determine the correct route based on authentication
      let analysisRoute;
      if (isAdminAuthenticated) {
        analysisRoute = '/admin/analysis';
      } else if (isUserAuthenticated) {
        analysisRoute = '/analysis';
      } else {
        // User is not authenticated, redirect to login
        showToast.error('Please log in to view analysis');
        navigate('/login');
        return;
      }
      
      // Navigate to Analysis page
      navigate(analysisRoute);
    } catch (error) {
      console.error('Navigation error:', error);
      showToast.error('Failed to navigate to analysis page');
    }
  };

  // Sort function
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Use patients directly from backend (already filtered and paginated)
  const paginatedPatients = patientsWithScans;

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, riskFilter]);

  // Debounced search and filter effect - single useEffect to prevent multiple calls
  useEffect(() => {
    // Clear any existing timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // Debounce search, but immediately trigger for other filters
    const delay = searchTerm ? 500 : 0;
    
    fetchTimeoutRef.current = setTimeout(() => {
      fetchPatientsWithScans();
    }, delay);

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [currentPage, searchTerm, riskFilter, sortField, sortDirection]);
  return (
    <>
      <div className='custom-card patient-table-section'>
        <div className='custom-card-header'>
          <div className='custom-card-header-text'>
            <h2>Patient Management</h2>
            <p>Manage patient records, upload scans, and track disease progression</p>
          </div>
          <div className='custom-card-filters d-flex align-items-center gap-3'>
            <div className="custom-upload" onClick={() => setModalShow(true)}>
              <span className='icon'><img src={Images.UploadIcon} alt="Upload" className='img-fluid' /></span>
              <span className='text'>Upload Scans</span>
            </div>

          </div>
        </div>
        <div className="custom-table-wrapper">
          <Row className="table-toolbar g-2 align-items-center">
            <Col>
              <Form.Control 
                className="search" 
                placeholder="Search by Patient ID, name, or email..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setCurrentPage(1);
                    fetchPatientsWithScans();
                  }
                }}
              />
            </Col>
            <Col md="auto">
              <Form.Select 
                className="filter"
                value={riskFilter}
                onChange={(e) => {
                  setRiskFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option>All Risk</option>
                <option>Low</option>
                <option>Medium</option>
                <option>High</option>
              </Form.Select>
            </Col>
            <Col md={12} className="text-end">
              <small className="text-muted">
                Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalPatients)} of {totalPatients} patients
              </small>
            </Col>
          </Row>
          <Table hover responsive className="custom-table">
            <thead>
              <tr>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('name')}
                  style={{ cursor: 'pointer' }}
                >
                  Patient {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('diagnosis')}
                  style={{ cursor: 'pointer' }}
                >
                  Diagnosis {sortField === 'diagnosis' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('edss')}
                  style={{ cursor: 'pointer' }}
                >
                  EDSS Score {sortField === 'edss' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('risk')}
                  style={{ cursor: 'pointer' }}
                >
                  Risk Level {sortField === 'risk' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('totalScans')}
                  style={{ cursor: 'pointer' }}
                >
                  Total Scans {sortField === 'totalScans' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('lastScan')}
                  style={{ cursor: 'pointer' }}
                >
                  Latest T {sortField === 'lastScan' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingScans ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                    <Spinner animation="border" variant="primary" />
                    <div className="mt-2">Loading patient data...</div>
                  </td>
                </tr>
              ) : paginatedPatients.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                    <div>No patients found</div>
                    <small className="text-muted">Try adjusting your search or filter criteria</small>
                  </td>
                </tr>
              ) : (
                paginatedPatients.map((patient) => (
                  <tr key={patient.id}>
                    <td>
                      <div className="person" onClick={() => handleViewAnalysis(patient)}>
                        <div className="avatar">
                          <img src={Images.UserIcon} alt={patient.name} className="img-fluid rounded-circle" />
                        </div>
                        <div>
                          <div className="name">{patient.fullName} <span className="text-muted" style={{ fontSize: '0.85em' }}>({patient.patientId})</span></div>
                          <div className="muted">{patient.email !== 'N/A' ? patient.email : ''}</div>
                          <div className="muted">Age: {patient.age} • {patient.sex === 'M' ? 'Male' : patient.sex === 'F' ? 'Female' : 'N/A'}</div>
                        
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ whiteSpace: 'pre-line' }}>
                        {patient.diagnosis}
                      </div>
                    </td>
                    <td>
                      {patient.edss !== 'N/A' ? (
                        <div className="edss-score-display">
                          <div className="edss-score-text">
                            <span className="edss-value">{patient.edss}</span>
                            <span className="edss-max"> / 10.0</span>
                          </div>
                          <div className='custom-progress-bar'>
                            <span 
                              className='progress-value' 
                              style={{ 
                                width: `${Math.min((parseFloat(patient.edss) / 10) * 100, 100)}%` 
                              }}
                            ></span>
                          </div>
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td>
                      <RiskBadge level={patient.risk} />
                    </td>
                    <td>
                      <div className="text-center">
                        <div className="fw-bold">{patient.totalScans}</div>
                        <small className="text-muted">scans</small>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="fw-bold">{patient.lastScan}</div>
                        <small className="text-muted">{patient.lastScanDate}</small>
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div 
                        className="action-icon" 
                        onClick={() => handleViewAnalysis(patient)}
                        style={{ cursor: 'pointer' }}
                        title="View Analysis"
                      >
                        <img src={Images.EyeIcon} alt="View Analysis" className='img-fluid' />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                <small className="text-muted">
                  Page {currentPage} of {totalPages} • {totalPatients} total patients
                </small>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="px-3 py-2">
                  {currentPage} / {totalPages}
                </span>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <CustomModal
        className='upload-scan-modal'
        show={modalShow}
        onHide={closeModal}
        centered={true}
        closeButton={false}
        showFooter = {false}
        customHeader={
          <>
            <h4 id="contained-modal-title-vcenter">Upload Medical Scans</h4>
            <p>Upload DICOM or NIfTI files for AI analysis</p>
            <button className='close-icon' onClick={closeModal}><SvgIcon src={Images.CloseIcon} className="icon " /></button>
          </>
        }
        footerButtons={[]}
      >
          {!scanData ?
          <div className="upload-form-container" style={{ position: 'relative' }}>
            {uploading && (
              <div className="upload-progress-overlay">
                <div className="upload-progress-content">
                  <Spinner animation="border" variant="primary" />
                  <div className="upload-progress-percentage">{uploadProgress}%</div>
                  <p className="upload-progress-text">
                    {uploadProgress < 100 
                      ? 'Uploading scan file...' 
                      : 'Analysis in progress - this may take up to 3 minutes for large files'
                    }
                  </p>
                  <div className="upload-progress-bar">
                    <div 
                      className="upload-progress-bar-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  {uploadProgress === 100 && (
                    <div className="analysis-warning">
                      <small className="text-muted">
                        ⏱️ Large medical imaging files require extensive processing time. 
                        Please keep this window open and be patient.
                      </small>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <Form.Group className="form-field" controlId="patientId">
              <Form.Label className="d-flex justify-content-between align-items-center">
                <span>Select Patient</span>
                <button
                  type="button"
                  className="btn-patient"
                  onClick={() => {
                    setShowCreatePatientForm(!showCreatePatientForm);
                  }}
                  disabled={uploading || loadingPatients}
                  style={{ 
                    fontSize: '1.2rem', 
                    textDecoration: 'none',
                    border: 'none',
                    background: '#3461d6',
                    cursor: uploading || loadingPatients ? 'not-allowed' : 'pointer'
                  }}
                  title={showCreatePatientForm ? 'Cancel' : 'Create New Patient'}
                >
                  {showCreatePatientForm ? (
                    <span style={{ color: '#fff' }}>✕</span>
                  ) : (
                    <span style={{ color: '#fff' }}>+ Add Patient</span>
                  )}
                </button>
              </Form.Label>

              {/* Create patient form */}
              {showCreatePatientForm && (
                <div className="border p-3 mb-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                  <h6 className="mb-3">Create New Patient</h6>
                  <Form.Group className="mb-2">
                    <Form.Label>Patient Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter patient name"
                      value={newPatientData.name}
                      onChange={(e) => setNewPatientData(prev => ({ ...prev, name: e.target.value }))}
                      disabled={creatingPatient}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Email (Optional)</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter email (optional)"
                      value={newPatientData.email}
                      onChange={(e) => setNewPatientData(prev => ({ ...prev, email: e.target.value }))}
                      disabled={creatingPatient}
                    />
                  </Form.Group>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleCreatePatient}
                    disabled={creatingPatient || !newPatientData.name.trim()}
                  >
                    {creatingPatient ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Creating...
                      </>
                    ) : (
                      'Create Patient'
                    )}
                  </button>
                </div>
              )}

              {/* Patient dropdown */}
              <Form.Select 
                className="form-control"
                name="patientId"
                value={formData.patientId}
                onChange={handleInputChange}
                disabled={uploading || loadingPatients}
              >
                <option value="">
                  {loadingPatients ? 'Loading patients...' : 'Choose a patient...'}
                </option>
                {patients.map((patient) => (
                  <option key={patient.id} value={patient.id}>
                    {patient.patientId} - {patient.name} {patient.email ? `(${patient.email})` : ''}
                  </option>
                ))}
              </Form.Select>
              {loadingPatients && (
                <div className="mt-2">
                  <Spinner animation="border" size="sm" /> Loading patients...
                </div>
              )}
              {!loadingPatients && patients.length === 0 && (
                <div className="mt-2 text-muted">
                  <small>No patients found. Click the + icon to create a new patient.</small>
                </div>
              )}
            </Form.Group>

            <Form.Group className="form-field" controlId="age">
              <Form.Label>Age at the time of scan</Form.Label>
              <Form.Control
                type="number"
                className="form-control"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                disabled={uploading}
                placeholder="Enter age (e.g., 30.9)"
                min="0"
                max="120"
                step="0.1"
              />
            </Form.Group>

            <Form.Group className="form-field" controlId="sex">
              <Form.Label>Sex</Form.Label>
              <Form.Select 
                className="form-control"
                name="sex"
                value={formData.sex}
                onChange={handleInputChange}
                disabled={uploading}
              >
                <option value="">Select sex...</option>
                <option value="M">Male (M)</option>
                <option value="F">Female (F)</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="form-field" controlId="msSubType">
              <Form.Label>MS Sub Type</Form.Label>
              <Form.Select 
                className="form-control"
                name="msSubType"
                value={formData.msSubType}
                onChange={handleInputChange}
                disabled={uploading}
              >
                <option value="">Select MS sub type...</option>
                <option value="RRMS">Relapsing-Remitting MS (RRMS)</option>
                <option value="SPMS">Secondary-Progressive MS (SPMS)</option>
                <option value="PPMS">Primary-Progressive MS (PPMS)</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="form-field" controlId="edssScore">
              <Form.Label>EDSS Score (0-10)</Form.Label>
              <Form.Control
                type="number"
                className="form-control"
                name="edssScore"
                value={formData.edssScore}
                onChange={handleInputChange}
                disabled={uploading}
                placeholder="Enter EDSS score (e.g., 2.5)"
                min="0"
                max="10"
                step="0.1"
              />
            </Form.Group>

            <div className="file-upload-section">
              <h6>Upload Scan Files</h6>
              
              {/* FLAIR File Upload */}
              <div className="mb-3">
                <label className="form-label">FLAIR File</label>
                <div 
                  className={`custom-upload-input ${selectedFlairFile ? 'has-file' : ''} ${isDragOverFlair ? 'drag-over' : ''}`}
                  onDragOver={(e) => handleDragOver(e, 'flair')}
                  onDragLeave={(e) => handleDragLeave(e, 'flair')}
                  onDrop={(e) => handleDrop(e, 'flair')}
                  onClick={() => handleUploadAreaClick('flair')}
                  style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}
                >
                  <SvgIcon className="icon" src={Images.UploadIcon} />
                  <p>
                    {isDragOverFlair ? 'Drop the FLAIR file here' : 'Drop FLAIR file here or click to browse'}
                  </p>
                  <span>Supports {FILE_UPLOAD.DISPLAY_FORMATS} (Max 1GB)</span>
                  
                  {selectedFlairFile && (
                    <div className="selected-file-info">
                      <div className="file-details">
                        <strong>Selected:</strong> {selectedFlairFile.name}
                        <br />
                        <small>Size: {(selectedFlairFile.size / (1024 * 1024)).toFixed(2)} MB</small>
                      </div>
                      <button 
                        type="button" 
                        className="btn btn-sm btn-outline-danger remove-file-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSelectedFile('flair');
                        }}
                        disabled={uploading}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  
                  <input 
                    type='file' 
                    name='flairFile'
                    ref={flairFileInputRef}
                    onChange={(e) => handleFileSelect(e, 'flair')}
                    disabled={uploading}
                    accept=".nii,.gz"
                    style={{ display: 'none' }}
                  />
                </div>
              </div>

              {/* T1 File Upload */}
              <div className="mb-3">
                <label className="form-label">T1 File</label>
                <div 
                  className={`custom-upload-input ${selectedT1File ? 'has-file' : ''} ${isDragOverT1 ? 'drag-over' : ''}`}
                  onDragOver={(e) => handleDragOver(e, 't1')}
                  onDragLeave={(e) => handleDragLeave(e, 't1')}
                  onDrop={(e) => handleDrop(e, 't1')}
                  onClick={() => handleUploadAreaClick('t1')}
                  style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}
                >
                  <SvgIcon className="icon" src={Images.UploadIcon} />
                  <p>
                    {isDragOverT1 ? 'Drop the T1 file here' : 'Drop T1 file here or click to browse'}
                  </p>
                  <span>Supports {FILE_UPLOAD.DISPLAY_FORMATS} (Max 1GB)</span>
                  
                  {selectedT1File && (
                    <div className="selected-file-info">
                      <div className="file-details">
                        <strong>Selected:</strong> {selectedT1File.name}
                        <br />
                        <small>Size: {(selectedT1File.size / (1024 * 1024)).toFixed(2)} MB</small>
                      </div>
                      <button 
                        type="button" 
                        className="btn btn-sm btn-outline-danger remove-file-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSelectedFile('t1');
                        }}
                        disabled={uploading}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  
                  <input 
                    type='file' 
                    name='t1File'
                    ref={t1FileInputRef}
                    onChange={(e) => handleFileSelect(e, 't1')}
                    disabled={uploading}
                    accept=".nii,.gz"
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </div>

            <button 
              className='btn btn-primary' 
              onClick={handleUploadScan}
              disabled={uploading || !formData.patientId || !formData.age || !formData.sex || !formData.msSubType || !formData.edssScore || !selectedFlairFile || !selectedT1File}
            >
              {uploading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {uploadProgress < 100 
                    ? `Uploading... ${uploadProgress}%` 
                    : 'Processing... (up to 3 min)'
                  }
                </>
              ) : (
                'Process & Analyze'
              )}
            </button>
          </div>
            :
            <div className='patient-data'>
              <div className='patient-details'>
                <div className='patient-image'>
                  <img src={Images.UserIcon} alt='Patient Icon' className='img-fluid' />
                </div>
                <div>
                  <span>Patient ID</span>
                  <span>{(() => {
                    return uploadedScanData?.patientId || 'N/A';
                  })()}</span>
                </div>
                <div>
                  <span>Age/Sex</span>
                  <span>{uploadedScanData?.age} years, {uploadedScanData?.sex === 'M' ? 'Male' : 'Female'}</span>
                </div>
                <div>
                  <span>Diagnosis</span>
                  <span>
                    {uploadedScanData?.msSubType === 'RRMS' ? 'Relapsing-Remitting MS' :
                     uploadedScanData?.msSubType === 'SPMS' ? 'Secondary-Progressive MS' :
                     uploadedScanData?.msSubType === 'PPMS' ? 'Primary-Progressive MS' :
                     uploadedScanData?.msSubType || 'Unknown'}
                  </span>
                </div>
                <div>
                  <span>EDSS Score</span>
                  <span>{uploadedScanData?.edssScore}</span>
                </div>
              </div>
              <div className='recent-scans'>
                <h5>Recent Brain Scans</h5>
                <div className='scans-list'>
                  {recentScanImages.length > 0 ? (
                    (() => {
                      const currentScan = recentScanImages[0];
                      const scanViews = [
                        { 
                          name: 'Multiplanar', 
                          image: currentScan.multiplanarImage,
                          alt: 'Multiplanar View'
                        },
                        { 
                          name: 'Axial', 
                          image: currentScan.axialImage,
                          alt: 'Axial View'
                        },
                        { 
                          name: 'Coronal', 
                          image: currentScan.coronalImage,
                          alt: 'Coronal View'
                        },
                        { 
                          name: 'Sagittal', 
                          image: currentScan.sagittalImage,
                          alt: 'Sagittal View'
                        }
                      ];
                      
                      return scanViews.map((view, index) => (
                        <div key={index} className='image'>
                          {view.image ? (
                            <img 
                              src={view.image} 
                              alt={view.alt} 
                              className='img-fluid'
                              onError={(e) => {
                                e.target.src = Images.Scan;
                              }}
                            />
                          ) : (
                            <img src={Images.Scan} alt="No scan available" className='img-fluid' />
                          )}
                          <div className="scan-info">
                            <span className="timepoint">{view.name}</span>
                            <span className="scan-date">{currentScan.timepoint}</span>
                          </div>
                        </div>
                      ));
                    })()
                  ) : (
                    // Show placeholder images if no recent scans
                    Array.from({ length: 4 }, (_, index) => (
                      <div key={index} className='image'>
                        <img src={Images.Scan} alt="No scan available" className='img-fluid' />
                        <div className="scan-info">
                          <span className="timepoint">N/A</span>
                          <span className="scan-date">No scans</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <span className='last-date'>Last scan: {uploadedScanData?.scanDate ? new Date(uploadedScanData.scanDate).toLocaleDateString('en-GB') : 'N/A'}</span>
              </div>
              <div className='clinical-notes'>
                <h5>Clinical Notes</h5>
                <ul>
                  <li>{uploadedScanData?.scanDate ? new Date(uploadedScanData.scanDate).toLocaleDateString('en-GB') : 'Today'}:</li>
                  <li>Patient: {uploadedScanData?.patientName || 'Unknown'}</li>
                  <li>EDSS Score: {uploadedScanData?.edssScore} - {uploadedScanData?.edssScore >= 6 ? 'High risk' : uploadedScanData?.edssScore >= 3 ? 'Medium risk' : 'Low risk'}</li>
                </ul>
              </div>
            </div>
          }
      </CustomModal>
    </>
  );
}